import { lazy } from 'react'
import { Route, Routes } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  DistributorProductVariantsDocument,
  GetFeatureAvailabilitiesDocument,
  ItAssetDetailsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { buildNavigation } from 'modules/core/lib/react-router'

import { createProductCatalogQueryContext } from 'modules/it_management/lib/graphqlProductCatalogContext'

const RootPage = lazy(() => import('modules/it_management/pages/RootPage'))

const ItManagementIndexPage = lazy(() => import('../pages/index'))
const PeoplePage = lazy(() => import('../pages/PeoplePage'))
const DeviceCatalogPage = lazy(() => import('../pages/DeviceCatalogPage'))
const DeviceCatalogDetailPage = lazy(() => import('../pages/DeviceCatalogDetailPage'))
const UniversalImporterItAssetsModalPage = lazy(
  () => import('../pages/UniversalImporterItAssetsModalPage')
)
const UniversalImporterItAssetsResultPage = lazy(
  () => import('../pages/UniversalImporterItAssetsResultPage')
)
const EditItAssetPopup = lazy(() => import('../pages/EditItAssetPopup/index'))
const AssetDetailsLayout = lazy(() => import('../pages/AssetDetailsPage/AssetDetailsLayout'))
const AssetDetailsOverview = lazy(() => import('../pages/AssetDetailsPage/AssetDetailsOverview'))
const AssetDocumentsPage = lazy(() => import('../pages/AssetDetailsPage/AssetDocumentsPage'))

export const itManagementModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.deviceCatalog.path,
      element: <RootPage />,
      handle: {
        module: 'device_catalog',
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: GetFeatureAvailabilitiesDocument,
            variables: {
              modules: [AvailableModules.it_management_v2],
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const featuresData = data?.companies.featureAvailabilities ?? []
            const isItManagementEnabled = isFeatureAvailabilityEnabled(
              featuresData,
              Features.it_management_v2_core
            )

            // If user doesn't have permission, return minimal metadata (no title)
            if (!isItManagementEnabled) {
              return {
                title: '',
              }
            }

            return {
              title: i18n.t('it_management.device_catalog.title'),
              description: i18n.t('it_management.device_catalog.title'),
            }
          })
        },
      },
      children: [
        {
          path: '',
          element: <DeviceCatalogPage />,
        },
        {
          path: navigation.deviceCatalog.variantId.path,
          element: <DeviceCatalogDetailPage />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: DistributorProductVariantsDocument,
                variables: { ids: params.variantId ? [params.variantId] : [] },
                context: createProductCatalogQueryContext(false),
              }).map(({ data }) => {
                const variant = data?.itManagement?.distributorProductVariantsConnection?.nodes?.[0]
                const deviceName = variant?.product?.name
                  ? variant.product.name
                  : variant?.sku ||
                    variant?.id ||
                    i18n.t('it_management.device_catalog.detail.title')

                const isInStock =
                  variant?.stock !== null && variant?.stock !== undefined && variant?.stock > 0

                const deliveryInfo =
                  variant?.deliveryConditions &&
                  variant.deliveryConditions.trim() !== '' &&
                  variant.deliveryConditions !== '-'
                    ? variant.deliveryConditions
                    : i18n.t('it_management.device_catalog.confirmed_delivery_message')

                return {
                  title: deviceName || i18n.t('it_management.device_catalog.detail.title'),
                  headerComponent: (
                    <ResourceHeader
                      title={deviceName || i18n.t('it_management.device_catalog.detail.title')}
                      status={
                        isInStock
                          ? {
                              label: i18n.t('it_management.common.status'),
                              text: i18n.t('it_management.assets.status.in_stock'),
                              variant: 'positive',
                            }
                          : {
                              label: i18n.t('it_management.common.status'),
                              text: i18n.t('it_management.assets.status.out_of_stock'),
                              variant: 'critical',
                            }
                      }
                      metadata={[
                        {
                          label: i18n.t('it_management.device_catalog.delivery_title'),
                          value: {
                            type: 'text' as const,
                            content: deliveryInfo,
                          },
                        },
                      ]}
                    />
                  ),
                }
              })
            },
          },
        },
      ],
    },
    {
      path: navigation.it_management.path,
      element: <RootPage />,
      handle: {
        module: 'it_management',
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: GetFeatureAvailabilitiesDocument,
            variables: {
              modules: [AvailableModules.it_management_v2],
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const featuresData = data?.companies.featureAvailabilities ?? []
            const isItManagementEnabled = isFeatureAvailabilityEnabled(
              featuresData,
              Features.it_management_v2_core
            )

            // If user doesn't have permission, return minimal metadata (no title, no tabs)
            if (!isItManagementEnabled) {
              return {
                title: '',
                subNavigation: [],
              }
            }

            return {
              title: i18n.t('it_management.it_inventory.title'),
              description: i18n.t('it_management.it_inventory.title'),
              settings: navigation.settings.it_inventory.fullPath(),
              subNavigation: [
                {
                  id: 'devices',
                  title: i18n.t('it_management.routes.devices'),
                  href: navigation.it_management.fullPath(),
                  index: true,
                  isVisible: true,
                },
                {
                  id: 'people',
                  title: i18n.t('it_management.routes.people'),
                  href: navigation.it_management.fullPath() + '/people',
                  isVisible: true,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          path: '',
          element: <ItManagementIndexPage />,
          children: [
            {
              path: 'edit/:assetId',
              element: <EditItAssetPopup />,
            },
          ],
        },
        {
          path: 'people',
          element: <PeoplePage />,
        },
        {
          path: navigation.it_management.importAssetsResult.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterItAssetsResultPage />} />
            </Routes>
          ),
        },
        {
          path: navigation.it_management.importAssets.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterItAssetsModalPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('it_management.routes.assets_universal_importer'),
              headerComponent: (
                <ResourceHeader title={i18n.t('it_management.routes.assets_universal_importer')} />
              ),
            }),
          },
        },
        {
          path: navigation.it_management.assetId.path,
          element: <AssetDetailsLayout />,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              return watchQuery({
                query: ItAssetDetailsDocument,
                variables: {
                  id: params.assetId || '',
                },
              }).map(({ data }) => {
                const asset = data?.itManagement?.itAsset
                const assetName = asset?.itAssetModel?.name || asset?.serialNumber
                return {
                  title: assetName || i18n.t('it_management.routes.asset_details'),
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <AssetDetailsOverview />,
            },
            {
              path: navigation.it_management.assetId.documents.path,
              element: <AssetDocumentsPage />,
            },
          ],
        },
      ],
    },
  ]
})
